import _ from 'lodash';
import React from 'react';
import { TimeRangeField, TextCollectionFieldType } from '@scale/shared/types/params/textcollection';
import {
  TimeRangeFieldAnnotation,
  UnitFieldAnnotation,
} from '@scale/shared/types/response/textcollection';
import { IconButton, TextButton } from 'design-system';
import { makeStyles } from 'design-system/styles';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText, TextField } from '@material-ui/core';
import { getHelperText } from './helperText';
import { getFieldMaxResponses, getFieldMinResponses } from '@scale/shared/textcollection/defaults';
import Autocomplete from '@material-ui/lab/Autocomplete';

interface TimeRangeFieldSectionProps {
  field: TimeRangeField;
  annotation: TimeRangeFieldAnnotation;
  setAnnotation: (annotation: TimeRangeFieldAnnotation) => void;
  disabled?: boolean;
  unitResponsesByField: Record<string, UnitFieldAnnotation>;
}

const labelForRangeIndex = ['Start', 'End'];

export const getShiftedTime = (seconds: number): { time: number; hourType: string } => {
  const hours = Math.floor(seconds / 3600);
  if (seconds > 12 * 3600) {
    return {
      time: seconds - 12 * 3600,
      hourType: hours === 24 ? 'AM' : 'PM',
    };
  } else {
    return {
      time: seconds,
      hourType: hours === 12 ? 'PM' : 'AM',
    };
  }
};

const formatTime = (timestamp: number, field: TimeRangeField): string => {
  const { time, hourType } = getShiftedTime(timestamp);
  const hours = (Math.floor(time / 3600) || 12).toString();
  const minutes = Math.floor((time % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0');
  let timeStr = `${hours}:${minutes}`;
  // Only show seconds if it's within the increments
  const increment = (field.increment_seconds || 1) % 60;
  if (increment > 0) {
    timeStr += `:${seconds}`;
  }
  return `${timeStr} ${hourType}`;
};

const isInitialResponse = (response: [number, number][], field: TimeRangeField) => {
  if (field.defaults_seconds) {
    return response.length === 1 && _.isEqual(field.defaults_seconds, response[0]);
  } else {
    return response.length === 1 && response[0][0] === 0 * 3600 && response[0][1] === 24 * 3600;
  }
};

export default function TimeRangeFieldSection(props: TimeRangeFieldSectionProps) {
  const [changed, setChanged] = React.useState(
    !isInitialResponse(props.annotation.response, props.field),
  );
  const responseCount = props.annotation.response.length;
  const maxResponses = getFieldMaxResponses(props.field);
  const minResponses = getFieldMinResponses(props.field);
  const onChange = function (index: number, rangeIndex: 0 | 1, value: number): void {
    const response = _.cloneDeep(props.annotation.response);
    response[index][rangeIndex] = value;
    const annotation: TimeRangeFieldAnnotation = {
      ...props.annotation,
      response,
    };
    setChanged(true);
    props.setAnnotation(annotation);
  };
  const removeResponse = (index: number) => {
    const response = props.annotation.response.slice();
    response.splice(index, 1);
    const timeRangeAnnotation: TimeRangeFieldAnnotation = {
      type: TextCollectionFieldType.TimeRange,
      field_id: props.field.field_id,
      response,
    };
    setChanged(true);
    props.setAnnotation(timeRangeAnnotation);
  };

  const addResponse = () => {
    if (responseCount < maxResponses) {
      const response = _.cloneDeep(props.annotation.response);
      response.push(props.field.defaults_seconds || [0, 24 * 3600]);
      const timeRangeAnnotation: TimeRangeFieldAnnotation = {
        type: TextCollectionFieldType.TimeRange,
        field_id: props.field.field_id,
        response,
      };
      setChanged(true);
      props.setAnnotation(timeRangeAnnotation);
    }
  };
  React.useEffect(() => {
    if (!changed) {
      const copyDefault =
        props.field.default_from_field &&
        props.unitResponsesByField[props.field.default_from_field];
      if (copyDefault && copyDefault.type === TextCollectionFieldType.TimeRange) {
        const targetResponse = copyDefault.response;
        if (
          !isInitialResponse(targetResponse, props.field) &&
          !_.isEqual(copyDefault.response, props.annotation.response)
        ) {
          props.setAnnotation({
            ..._.cloneDeep(copyDefault),
            field_id: props.field.field_id,
            type: TextCollectionFieldType.TimeRange,
          });
        }
      }
    }
  }, [props.setAnnotation, props.annotation, changed, props.field, props.unitResponsesByField]);
  const classes = useStyles();
  const selectForRangeIndex = (
    response: [number, number],
    index: number,
    rangeIndex: 0 | 1,
  ): JSX.Element => (
    <Autocomplete
      value={{
        seconds: response[rangeIndex],
        label: formatTime(response[rangeIndex], props.field),
      }}
      options={_.range(0, 24 * 3600, props.field.increment_seconds || 1).map(seconds => ({
        seconds,
        label: formatTime(seconds, props.field),
      }))}
      getOptionLabel={entry => entry.label}
      renderOption={entry => <span>{entry.label}</span>}
      renderInput={params => (
        <TextField
          {...params}
          label={`${labelForRangeIndex[rangeIndex]} Time`}
          variant="outlined"
          classes={{ root: classes.autocomplete }}
        />
      )}
      disableClearable
      onChange={(
        event: React.ChangeEvent<{}>,
        value: { seconds: number; label: string } | null,
      ) => {
        onChange(index, rangeIndex, value?.seconds || 0);
      }}
    />
  );
  const selects = props.annotation.response.map((response: [number, number], index: number) => {
    return (
      <div key={`timerange-select-${index}`}>
        <div key={index} className={classes.row}>
          <div className={classes.selects}>
            <div className={classes.fromSelect}>{selectForRangeIndex(response, index, 0)}</div>
            to
            <div className={classes.toSelect}>{selectForRangeIndex(response, index, 1)}</div>
          </div>
          {(index > 0 || minResponses < 1) && responseCount > minResponses && (
            <IconButton
              className={classes.inlineButton}
              disabled={props.disabled}
              onClick={() => removeResponse(index)}
            >
              <TrashIcon />
            </IconButton>
          )}
        </div>
      </div>
    );
  });
  return (
    <>
      <div className={classes.title}>{props.field.title}</div>
      <FormHelperText classes={{ root: classes.helperText }}>
        {getHelperText({ description: props.field.description })}
      </FormHelperText>
      {selects}
      <div>
        {responseCount < maxResponses && (
          <TextButton
            classes={{ root: classes.inlineAction }}
            startIcon={<PlusIcon />}
            disabled={props.disabled || responseCount >= maxResponses}
            onClick={addResponse}
          >
            Add {props.field.title}
          </TextButton>
        )}
      </div>
    </>
  );
}

const useStyles = makeStyles({
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '8px',
  },
  helperText: {
    marginBottom: 8,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  timeText: {
    color: '#000000',
    fontSize: '13px',
    margin: 8,
  },
  selects: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    flexDirection: 'row',
    maxWidth: 376,
    marginTop: 8,
    marginBottom: 8,
  },
  fromSelect: {
    marginRight: 8,
    display: 'inline',
  },
  toSelect: {
    marginLeft: 8,
    display: 'inline',
  },
  autocomplete: {
    width: 136,
  },
  option: {
    width: 126,
  },
  inlineAction: {
    color: '#2B99D8',
    marginRight: 8,
    marginBottom: 8,
    marginTop: 8,
  },
  inlineButton: {
    color: '#2B99D8',
    paddingRight: '8px !important',
    paddingLeft: '8px !important',
    margin: 0,
  },
});

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.8 6.80039H9.20003V3.20039C9.20003 2.75864 8.84177 2.40039 8.40002 2.40039H7.60002C7.15827 2.40039 6.80002 2.75864 6.80002 3.20039V6.80039H3.20002C2.75827 6.80039 2.40002 7.15864 2.40002 7.60039V8.40039C2.40002 8.84214 2.75827 9.20039 3.20002 9.20039H6.80002V12.8004C6.80002 13.2421 7.15827 13.6004 7.60002 13.6004H8.40002C8.84177 13.6004 9.20003 13.2421 9.20003 12.8004V9.20039H12.8C13.2418 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2418 6.80039 12.8 6.80039Z"
        fill="#2B99D8"
      />
    </svg>
  );
}

const TrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M13.2 2.39961H10.2L9.96503 1.93211C9.91524 1.83217 9.83856 1.74809 9.74361 1.68935C9.64865 1.63061 9.53918 1.59953 9.42753 1.59961H6.57002C6.45862 1.59919 6.34935 1.63015 6.25473 1.68895C6.16011 1.74776 6.08396 1.83203 6.03502 1.93211L5.80002 2.39961H2.80002C2.69394 2.39961 2.5922 2.44176 2.51718 2.51677C2.44217 2.59179 2.40002 2.69353 2.40002 2.79961V3.59961C2.40002 3.7057 2.44217 3.80744 2.51718 3.88246C2.5922 3.95747 2.69394 3.99961 2.80002 3.99961H13.2C13.3061 3.99961 13.4079 3.95747 13.4829 3.88246C13.5579 3.80744 13.6 3.7057 13.6 3.59961V2.79961C13.6 2.69353 13.5579 2.59179 13.4829 2.51677C13.4079 2.44176 13.3061 2.39961 13.2 2.39961ZM3.73002 13.2746C3.7491 13.5793 3.88356 13.8652 4.10604 14.0742C4.32851 14.2832 4.62227 14.3996 4.92752 14.3996H11.0725C11.3778 14.3996 11.6715 14.2832 11.894 14.0742C12.1165 13.8652 12.2509 13.5793 12.27 13.2746L12.8 4.79961H3.20002L3.73002 13.2746Z" />
    </svg>
  );
};
